import { cn } from "@vigia/shared";

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
}

/**
 * Vigía brand mark + wordmark.
 *
 * Identity: a **watchful radar-eye** — concentric scope rings sweeping around a
 * central watching eye, the signature of a benevolent lookout that sees and
 * controls the whole fleet from one panel.
 *
 * THEME-SAFE BY CONSTRUCTION (gotcha confirmed across the portfolio): drawing
 * mark parts with `text-accent` / raw accent on the page surface makes them
 * vanish under pale/custom themes. Instead the entire mark is drawn with
 * `currentColor` inside a SOLID `bg-accent` chip whose text color is
 * `text-accent-foreground` — a pair the theme engine GUARANTEES to contrast.
 * The wordmark uses `text-foreground` (always legible over the app background),
 * so it never depends on the accent hue for legibility.
 */
function BrandMark(props: { px: number }) {
  const glyph = () => Math.round(props.px * 0.64);
  return (
    <div
      class="grid place-items-center rounded-2xl bg-accent text-accent-foreground shadow-sm ring-1 ring-black/5"
      style={{ width: `${props.px}px`, height: `${props.px}px` }}
      aria-hidden="true"
    >
      {/* Radar-eye: scope rings + a sweep + the watching pupil, drawn entirely
       * with currentColor (inherited from text-accent-foreground) so contrast
       * is guaranteed on any theme. */}
      <svg
        width={glyph()}
        height={glyph()}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="shrink-0"
      >
        {/* Outer scope ring */}
        <circle cx="12" cy="12" r="9.2" stroke-width="1.7" opacity="0.55" />
        {/* Inner scope ring */}
        <circle cx="12" cy="12" r="5.6" stroke-width="1.5" opacity="0.8" />
        {/* Radar sweep (from centre to the rim) */}
        <path d="M12 12 L20.2 8.1" stroke-width="1.7" opacity="0.7" />
        {/* Crosshair ticks */}
        <path d="M12 1.6v2.4M12 20v2.4M1.6 12H4M20 12h2.4" stroke-width="1.6" opacity="0.5" />
        {/* Watching pupil (the eye), solid */}
        <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
      </svg>
    </div>
  );
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";

  return (
    <div
      class={cn(
        "flex items-center gap-3 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <BrandMark px={32} />
          <span class="text-xl font-extrabold tracking-tight text-foreground">
            {/* oxlint-disable-next-line lingui/no-unlocalized-strings -- brand (proper noun), not translatable */}
            Vig<span class="font-semibold text-muted-foreground">ía</span>
          </span>
        </>
      ) : (
        <>
          <div class="mb-2 transition-transform hover:scale-105 active:scale-95 duration-300">
            <BrandMark px={64} />
          </div>
          <h1 class="text-3xl font-extrabold tracking-tight text-foreground">
            {/* oxlint-disable-next-line lingui/no-unlocalized-strings -- brand (proper noun), not translatable */}
            Vig<span class="text-muted-foreground">ía</span>
          </h1>
        </>
      )}
    </div>
  );
}
